import * as React from "react";
import { BookOpen, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Modal } from "@multiagentbook/ui";
import { changelogEntries } from "./changelog-data";

// Master switch. Flip to false to hide the banner everywhere regardless of
// content/freshness.
const ANNOUNCEMENT_ENABLED = true;

// Auto-hide the banner once the latest update is older than this many days, so
// it disappears on its own when there is nothing genuinely new to announce.
const ANNOUNCEMENT_MAX_AGE_DAYS = 30;

const DISMISS_KEY = "mab-announcement-dismissed";

interface AnnouncementProps {
  className?: string;
}

const Announcement: React.FC<AnnouncementProps> = ({ className = "" }) => {
  const { t, i18n } = useTranslation(["home", "changelog"]);

  // changelogEntries is sorted newest-first; the banner always reflects the
  // most recent update.
  const latest = changelogEntries[0];

  const [dismissed, setDismissed] = React.useState(false);
  const [showModal, setShowModal] = React.useState(false);
  React.useEffect(() => {
    try {
      setDismissed(localStorage.getItem(DISMISS_KEY) === latest?.id);
    } catch {
      /* localStorage unavailable - just show the banner */
    }
  }, [latest?.id]);

  if (!ANNOUNCEMENT_ENABLED || !latest) return null;

  // Freshness check
  const ageMs = new Date().getTime() - new Date(latest.date).getTime();
  const ageDays = ageMs / (1000 * 60 * 60 * 24);
  if (ageDays > ANNOUNCEMENT_MAX_AGE_DAYS) return null;

  if (dismissed) return null;

  // Prefer translated title/description from the changelog namespace; fall back
  // to the source-of-truth English copy if a translation is missing.
  const bundle = i18n.getResourceBundle(i18n.language, "changelog") as
    | { entries?: { id: string; title: string; description: string }[] }
    | undefined;
  const translated = bundle?.entries?.find((e) => e.id === latest.id);
  const title = translated?.title ?? latest.title;
  const description = translated?.description ?? latest.description;

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      localStorage.setItem(DISMISS_KEY, latest.id);
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  // Scroll to the "What's New" section. The id may be rendered in more than one
  // layout branch (mobile + desktop), so target the visible one.
  const scrollToWhatsNew = () => {
    setShowModal(false);
    const matches = Array.from(
      document.querySelectorAll<HTMLElement>('[id="whats-new"]')
    );
    const target = matches.find((el) => el.offsetParent !== null) ?? matches[0];
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setShowModal(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setShowModal(true);
          }
        }}
        className={`bg-accent/10 border border-accent/30 rounded-lg p-3 mb-6 cursor-pointer hover:bg-accent/15 transition-colors ${className}`}
      >
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-accent shrink-0" />
          {/* Truncated to a single line so a long title never wraps the banner */}
          <span className="text-sm text-primary min-w-0 flex-1 truncate">
            <span className="font-semibold text-accent">
              {t("announcement.new")}
            </span>{" "}
            {title}
          </span>
          <span className="text-sm text-accent font-medium whitespace-nowrap shrink-0 hidden sm:inline">
            {t("announcement.readMore")} →
          </span>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label={t("announcement.dismiss")}
            className="shrink-0 text-tertiary hover:text-primary transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={title}
        size="md"
        footer={
          <>
            <button
              type="button"
              onClick={scrollToWhatsNew}
              className={
                latest.ctaUrl
                  ? "px-4 py-2 text-secondary hover:text-primary transition-colors text-sm font-medium"
                  : "px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors text-sm font-medium"
              }
            >
              {t("announcement.viewAll")}
              {latest.ctaUrl ? "" : " →"}
            </button>
            {/* hover:text-white is load-bearing: the global `a:hover` rule
                repaints links with text-accent, which is this button's own
                background colour, so the label would vanish on hover. */}
            {latest.ctaUrl && (
              <a
                href={latest.ctaUrl}
                className="px-4 py-2 bg-accent text-white hover:text-white rounded-lg hover:bg-accent/90 transition-colors text-sm font-medium"
              >
                {t(latest.ctaLabelKey ?? "announcement.open")} →
              </a>
            )}
          </>
        }
      >
        <div className="p-6">
          <p className="text-secondary leading-relaxed">{description}</p>
        </div>
      </Modal>
    </>
  );
};

export default Announcement;
